import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// ========================================
// Types for Hindi Service
// ========================================

export type ProficiencyLevel = 'beginner' | 'intermediate' | 'advanced';

export interface HindiWord {
  character: string;
  transliteration: string;
  english: string;
}

export interface HindiLesson {
  lesson_id?: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  text: string;
  words: HindiWord[];
  created_at?: string;
  updated_at?: string;
}

export interface LessonSummary {
  lesson_id: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  created_at: string;
  updated_at: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// Helper function to extract level from lesson objective
export const extractLevelFromObjective = (objective: string): ProficiencyLevel | null => {
  const match = objective.match(/\[Level:\s*(Beginner|Intermediate|Advanced)\]/i);
  if (match) {
    return match[1].toLowerCase() as ProficiencyLevel;
  }
  return null;
};

// Helper function to format level display name
export const getLevelDisplayName = (level: ProficiencyLevel): string => {
  return level.charAt(0).toUpperCase() + level.slice(1);
};

// ========================================
// Hindi Service Class
// ========================================

export class HindiService {
  /**
   * Build system prompt for text generation based on level
   */
  private static buildTextPrompt(
    level: ProficiencyLevel,
    pastLessons?: LessonSummary[], 
    customInstruction?: string
  ): string {
    const levelConfigs = {
      beginner: {
        description: 'A1-A2 level (beginner learners)',
        charCount: '400-600',
        topics: 'basic greetings, self-introduction, family members, numbers (1-100), colors, simple shopping phrases, basic directions, daily objects, simple present tense',
        grammar: 'simple present tense, basic sentence structures, common postpositions',
        style: 'very simple sentences, repetitive structures for reinforcement'
      },
      intermediate: {
        description: 'B1-B2 level (intermediate learners)',
        charCount: '700-900',
        topics: 'daily conversations, travel experiences, work and professions, hobbies and interests, health and wellness, Indian festivals, daily routines, expressing opinions',
        grammar: 'past and future tenses, compound sentences, relative clauses, conjunctions',
        style: 'natural conversation flow, varied sentence structures'
      },
      advanced: {
        description: 'C1-C2 level (advanced learners)',
        charCount: '900-1200',
        topics: 'politics, philosophy, Hindi literature, scientific concepts, cultural analysis, abstract concepts, social issues, idioms and proverbs',
        grammar: 'all tenses, complex conditional sentences, passive voice, subjunctive mood, literary expressions',
        style: 'sophisticated vocabulary, native-like expressions, nuanced language'
      }
    };

    const config = levelConfigs[level];

    let prompt = `You are a Hindi language teacher creating reading passages for ${config.description} students.

Your task is to generate a Hindi reading passage in Devanagari script.

REQUIREMENTS:
- Create a passage of ${config.charCount} Devanagari characters
- ${config.description.toUpperCase()} difficulty level
- Topics: ${config.topics}
- Grammar focus: ${config.grammar}
- Writing style: ${config.style}
- Make the content engaging, practical, and cohesive
- The passage should tell a complete story or describe a situation
- Use proper Devanagari script (देवनागरी)
- For dialogue, use standard quotation marks (" ")
- Avoid using any characters that require escaping in JSON (backslashes, etc.)`;

    if (pastLessons && pastLessons.length > 0) {
      const historyContext = pastLessons
        .map(l => {
          const lessonLevel = extractLevelFromObjective(l.lesson_objective);
          const levelStr = lessonLevel ? ` [${getLevelDisplayName(lessonLevel)}]` : '';
          return `- "${l.lesson_name}"${levelStr}: ${l.lesson_objective.replace(/\[Level:\s*\w+\]\s*/, '')}`;
        })
        .join('\n');
      
      prompt += `\n\nPREVIOUS LESSONS COVERED:\n${historyContext}\n\n`;
      prompt += `Generate a NEW lesson that builds upon or complements these topics. `;
      prompt += `Avoid repeating the same vocabulary and topics from previous lessons.\n`;
    }

    prompt += `\n\nRESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "lesson_name": "Short lesson title in English (e.g., 'Meeting a Friend')",
  "lesson_objective": "Brief description of what this lesson teaches (e.g., 'Learn vocabulary for greetings and casual conversations with friends')",
  "text": "Complete Hindi text passage in Devanagari script (${config.charCount} characters)"
}

IMPORTANT:
- Focus on creating natural, flowing Hindi text in Devanagari script
- Use varied vocabulary and sentence structures appropriate for ${level} level
- Make sure the JSON is valid and properly formatted
- The text should be interesting and educational
- Use standard quotation marks for any dialogue or quoted speech`;

    return prompt;
  }

  /**
   * Build system prompt for vocabulary extraction
   */
  private static buildVocabularyPrompt(text: string, level: ProficiencyLevel): string {
    const vocabCounts = {
      beginner: '20-30',
      intermediate: '30-45',
      advanced: '40-60'
    };

    return `You are a Hindi language teacher analyzing a text for ${level} level students.

Your task is to extract and translate key vocabulary from the provided Hindi text.

HINDI TEXT (in Devanagari):
${text}

REQUIREMENTS:
- Extract important words and phrases that students should learn
- Include common words, useful phrases, and key vocabulary
- Provide accurate romanized transliteration (e.g., namaste, dhanyavaad)
- Use simple, consistent romanization (not IAST with diacritics)
- Provide clear English translations
- For multi-character words, keep them together (e.g., "नमस्ते" not "नम" and "स्ते" separately)
- Focus on words that are useful for ${level} level learners
- Include approximately ${vocabCounts[level]} vocabulary items

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "words": [
    {
      "character": "Hindi word in Devanagari",
      "transliteration": "romanized version (e.g., namaste)",
      "english": "English translation"
    }
  ]
}

IMPORTANT:
- Use simple romanization without diacritics (namaste, not namastē)
- Order words as they appear in the text
- Make sure the JSON is valid and properly formatted`;
  }

  /**
   * Generate Hindi text only (Step 1)
   */
  private static async generateText(
    level: ProficiencyLevel,
    pastLessons?: LessonSummary[], 
    customInstruction?: string
  ): Promise<{ lesson_name: string; lesson_objective: string; text: string }> {
    try {
      console.log('🇮🇳 [HindiService] Step 1: Generating Hindi text...');

      const systemPrompt = this.buildTextPrompt(level, pastLessons, customInstruction);
      
      let userMessage = 'Generate a new Hindi reading passage following the requirements.';
      if (customInstruction) {
        userMessage = `Generate a new Hindi reading passage following the requirements. Focus the lesson on: ${customInstruction}`;
      }
      
      const messages = [
        {
          role: 'user' as const,
          content: userMessage
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000,
        temperature: 0.8
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate Hindi text');
      }

      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [HindiService] AI text response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇮🇳 [HindiService] Failed to extract response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for text generation');
      }

      console.log('🇮🇳 [HindiService] Raw AI response length:', aiResponse.length);
      console.log('🇮🇳 [HindiService] First 200 chars:', aiResponse.substring(0, 200));

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      console.log('🇮🇳 [HindiService] After markdown removal, length:', jsonString.length);
      
      // Check if JSON appears to be truncated
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🇮🇳 [HindiService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let textData;
      try {
        textData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇮🇳 [HindiService] JSON parse error for text:', parseError);
        
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse text JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!textData || typeof textData !== 'object') {
        throw new Error('Invalid JSON response - expected an object');
      }
      
      if (!textData.lesson_name || !textData.lesson_objective || !textData.text) {
        throw new Error('Invalid text structure - missing lesson_name, lesson_objective, or text');
      }
      
      if (typeof textData.lesson_name !== 'string' || 
          typeof textData.lesson_objective !== 'string' || 
          typeof textData.text !== 'string') {
        throw new Error('Invalid text structure - lesson_name, lesson_objective, and text must be strings');
      }

      const cleanedText = textData.text;

      console.log('🇮🇳 [HindiService] Text generated successfully:', {
        lesson_name: textData.lesson_name,
        lesson_objective: textData.lesson_objective,
        textLength: cleanedText.length,
        textPreview: cleanedText.substring(0, 100) + '...'
      });

      // Add level prefix to lesson objective
      const levelPrefix = `[Level: ${getLevelDisplayName(level)}] `;
      const objectiveWithLevel = levelPrefix + textData.lesson_objective;

      return {
        lesson_name: textData.lesson_name,
        lesson_objective: objectiveWithLevel,
        text: cleanedText
      };
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error generating text:', error);
      throw error;
    }
  }

  /**
   * Generate vocabulary from Hindi text (Step 2)
   */
  private static async generateVocabulary(text: string, level: ProficiencyLevel): Promise<HindiWord[]> {
    try {
      console.log('🇮🇳 [HindiService] Step 2: Generating vocabulary...');

      const systemPrompt = this.buildVocabularyPrompt(text, level);
      
      const messages = [
        {
          role: 'user' as const,
          content: 'Extract and translate the key vocabulary from this text following the requirements.'
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000,
        temperature: 0.3
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate vocabulary');
      }

      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [HindiService] AI vocabulary response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇮🇳 [HindiService] Failed to extract vocabulary response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for vocabulary generation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      // Check if JSON appears to be truncated
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🇮🇳 [HindiService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let vocabData;
      try {
        vocabData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇮🇳 [HindiService] JSON parse error for vocabulary:', parseError);
        
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse vocabulary JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!vocabData || typeof vocabData !== 'object') {
        throw new Error('Invalid vocabulary JSON response - expected an object');
      }
      
      if (!Array.isArray(vocabData.words)) {
        throw new Error('Invalid vocabulary structure - words must be an array');
      }
      
      // Validate each word has required fields
      const validWords = vocabData.words.filter((word: any) => {
        return word && 
               typeof word.character === 'string' && 
               typeof word.transliteration === 'string' && 
               typeof word.english === 'string';
      });
      
      if (validWords.length === 0) {
        throw new Error('No valid vocabulary words found');
      }

      console.log('🇮🇳 [HindiService] Vocabulary generated successfully:', {
        totalWords: vocabData.words.length,
        validWords: validWords.length
      });

      return validWords;
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error generating vocabulary:', error);
      throw error;
    }
  }

  /**
   * Generate a new Hindi lesson using AI and save to database
   * @param level Proficiency level: beginner, intermediate, or advanced
   * @param selectedLessonIds Array of lesson IDs to include as context
   * @param customInstruction Optional custom topic instruction
   * @param onProgress Optional callback to track progress: 'text' | 'vocabulary'
   */
  public static async generateLesson(
    level: ProficiencyLevel,
    selectedLessonIds: string[] = [],
    customInstruction?: string,
    onProgress?: (stage: 'text' | 'vocabulary') => void
  ): Promise<HindiLesson> {
    try {
      console.log('🇮🇳 [HindiService] Generating new Hindi lesson...');

      // Fetch selected past lessons for context
      let pastLessons: LessonSummary[] = [];
      if (selectedLessonIds.length > 0) {
        console.log('🇮🇳 [HindiService] Fetching past lessons for context...');
        const allLessons = await this.getPastLessons(100);
        pastLessons = allLessons.lessons.filter(l => selectedLessonIds.includes(l.lesson_id));
      }

      // Step 1: Generate the Hindi text
      if (onProgress) onProgress('text');
      const { lesson_name, lesson_objective, text } = await this.generateText(level, pastLessons, customInstruction);

      // Step 2: Generate vocabulary from the text
      if (onProgress) onProgress('vocabulary');
      const words = await this.generateVocabulary(text, level);

      console.log('🇮🇳 [HindiService] Lesson generated successfully:', {
        lesson_name,
        lesson_objective,
        textLength: text.length,
        wordCount: words.length
      });

      // Step 3: Save lesson to database
      const savedLesson = await this.saveLesson({
        lesson_name,
        lesson_objective,
        category: 'hindi',
        text,
        words
      });

      return savedLesson;
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error generating lesson:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate Hindi lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save lesson to database
   */
  public static async saveLesson(lessonData: {
    lesson_name: string;
    lesson_objective: string;
    category: string;
    text: string;
    words: HindiWord[];
  }): Promise<HindiLesson> {
    try {
      const result = await apiCall('/lessons', {
        method: 'POST',
        body: JSON.stringify({
          category: lessonData.category,
          lesson_name: lessonData.lesson_name,
          lesson_objective: lessonData.lesson_objective,
          lesson_content: {
            text: lessonData.text,
            vocabulary: lessonData.words,
            grammar: null
          }
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const savedLesson: HindiLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return savedLesson;
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error saving lesson:', error);
      throw error;
    }
  }

  /**
   * Get past lessons for user (category: hindi)
   */
  public static async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=hindi&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lessons');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        lessons: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error fetching past lessons:', error);
      throw error;
    }
  }

  /**
   * Get specific lesson by ID
   */
  public static async getLessonById(lessonId: string): Promise<HindiLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: HindiLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error fetching lesson:', error);
      throw error;
    }
  }

  /**
   * Update lesson name
   */
  public static async updateLessonName(lessonId: string, newName: string): Promise<HindiLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ lesson_name: newName })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: HindiLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error updating lesson:', error);
      throw error;
    }
  }

  /**
   * Delete lesson
   */
  public static async deleteLesson(lessonId: string): Promise<void> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('🇮🇳 [HindiService] Error deleting lesson:', error);
      throw error;
    }
  }
}

